import { ninToYYYYMMDD } from '@storebrand-digital/storebrand-common-validation';
import { DateString } from 'Types/date.types';

const verifyDate = (date: string): void => {
  if (!isValidDateString(date)) {
    throw new Error('Invalid date string format. Expected format: DD.MM.YYYY');
  }
};

export const getAgeFromDateOfBirth = (dateOfBirth: Date): number => {
  const now = new Date();

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();

  const birthYear = dateOfBirth.getFullYear();
  const birthMonth = dateOfBirth.getMonth();
  const birthDay = dateOfBirth.getDate();

  let age = currentYear - birthYear;

  if (
    currentMonth < birthMonth ||
    (currentMonth === birthMonth && currentDay < birthDay)
  ) {
    age -= 1;
  }

  return age;
};

export const getAgeFromNin = (nin: string): number | undefined => {
  const dateStringFromNin = ninToYYYYMMDD(nin);
  if (dateStringFromNin === undefined) {
    return undefined;
  }
  return getAgeFromDateOfBirth(new Date(dateStringFromNin));
};

export const normalizeDate = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

export const isValidDateString = (dateStr: string): dateStr is DateString => {
  const regex = /^\d{2}\.\d{1,2}\.\d{4}$/;
  return regex.test(dateStr);
};

export const getNextDayDate = (dateStr: string): string => {
  verifyDate(dateStr);

  const [day, month, year] = dateStr.split('.').map(Number);
  const date = new Date(year, month - 1, day);

  date.setDate(date.getDate() + 1);

  const nextDay = String(date.getDate()).padStart(2, '0');
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextYear = date.getFullYear();

  return `${nextDay}.${nextMonth}.${nextYear}`;
};

export const getPreviousDayDate = (dateStr: DateString): string => {
  verifyDate(dateStr);

  const [day, month, year] = dateStr.split('.').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() - 1);

  const prevDay = String(date.getDate()).padStart(2, '0');
  const prevMonth = String(date.getMonth() + 1).padStart(2, '0');
  const prevYear = date.getFullYear();

  return `${prevDay}.${prevMonth}.${prevYear}`;
};

export const formatDateToDDMMYYYY = (date: Date): DateString => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}` as DateString;
};

const reverseDDMMYYYY = (date: string): string => {
  verifyDate(date);

  return date.split('.').reverse().join('.');
};

export const isAfterOrSameDate = (
  date: string,
  dateToCompare: string,
): boolean =>
  Date.parse(reverseDDMMYYYY(date)) >=
  Date.parse(reverseDDMMYYYY(dateToCompare));
